import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchHomepageSliderImages, type HomepageSliderImage } from '@/services/homepageSliderService';
import { logging } from '@/services/logging';

const SLIDE_INTERVAL_MS = 5500;

const getImageAspectRatio = (image: HomepageSliderImage | undefined): string => (
  image?.widthPx && image.heightPx ? `${image.widthPx} / ${image.heightPx}` : '16 / 9'
);

const HomeImageSlider = () => {
  const [images, setImages] = useState<ReadonlyArray<HomepageSliderImage>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate how much of the element is visible relative to the viewport
      // Progress 0: Element top is at window middle/bottom
      // Progress 1: Element bottom is at window top/middle
      const elementHeight = rect.height;
      const elementTop = rect.top;

      // We want to transition from top-aligned to bottom-aligned
      // while the slider is within the middle 60% of the screen
      const startTrigger = windowHeight * 0.8;
      const endTrigger = windowHeight * 0.2;

      const progress = Math.min(Math.max((startTrigger - elementTop) / (startTrigger - endTrigger + elementHeight), 0), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      try {
        const result = await fetchHomepageSliderImages();
        if (!cancelled) {
          setImages(result);
          setActiveIndex(0);
        }
      } catch (error) {
        logging.warn('Failed to load homepage slider images', { error });
      }
    };

    void loadImages();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isPaused || images.length < 2) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, SLIDE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [images.length, isPaused]);

  const activeImage = images[activeIndex];
  const hasControls = images.length > 1;
  const activeAspectRatio = getImageAspectRatio(activeImage);
  const previousIndex = hasControls ? (activeIndex - 1 + images.length) % images.length : activeIndex;
  const nextIndex = hasControls ? (activeIndex + 1) % images.length : activeIndex;
  const previousImage = images[previousIndex];
  const nextImage = images[nextIndex];

  const dotLabels = useMemo(
    () => images.map((image, index) => image.title || image.altText || `Slide ${index + 1}`),
    [images],
  );

  if (!activeImage) {
    return null;
  }

  const goToPrevious = () => {
    setActiveIndex((current) => (current - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setActiveIndex((current) => (current + 1) % images.length);
  };

  const renderSidePreview = (
    image: HomepageSliderImage | undefined,
    targetIndex: number,
    direction: 'Previous' | 'Next',
  ) => {
    if (!image) {
      return null;
    }

    // Calculate vertical offset to align with bottom of main image
    // Main image height is variable but capped at 360px on md+
    // Side image height is fixed at 180px
    const mainHeight = 360; 
    const sideHeight = 180;
    const maxOffset = mainHeight - sideHeight;
    const translateY = scrollProgress * maxOffset;

    return (
      <button
        type="button"
        className="group hidden h-[180px] shrink-0 overflow-hidden rounded-md border border-white/80 bg-brand-tan/10 opacity-75 shadow-[0_6px_16px_rgba(83,55,33,0.24)] transition hover:scale-[1.02] hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 md:block dark:border-transparent dark:shadow-[0_0_18px_rgba(251,191,36,0.24)] dark:focus:ring-amber-200"
        style={{ 
          aspectRatio: getImageAspectRatio(image),
          transform: `translateY(${translateY}px)`,
          transition: 'transform 0.1s ease-out, opacity 0.2s ease, scale 0.2s ease'
        }}
        onClick={() => setActiveIndex(targetIndex)}
        aria-label={`${direction} slider image: ${image.title || image.altText}`}
      >
        <img
          src={image.imageUrl}
          alt=""
          className="h-full w-full object-contain transition group-hover:brightness-105"
          loading="lazy"
        />
      </button>
    );
  };

  return (
    <section
      ref={containerRef}
      className="mt-10 w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      aria-label="Printed artwork showcase"
    >
      <div className="flex w-full items-start justify-center gap-4 lg:gap-5">
        {hasControls && renderSidePreview(previousImage, previousIndex, 'Previous')}
        <div
          className="relative mx-auto h-[220px] max-w-full overflow-hidden rounded-md border-2 border-white bg-brand-tan/10 shadow-[0_10px_24px_rgba(83,55,33,0.38)] ring-1 ring-brand-brown/10 sm:h-[300px] md:h-[360px] dark:border-transparent dark:shadow-[0_0_28px_rgba(251,191,36,0.38),0_0_68px_rgba(255,255,255,0.14)] dark:ring-transparent"
          style={{ aspectRatio: activeAspectRatio }}
        >
          {images.map((image, index) => (
            <img
              key={image.id}
              src={image.imageUrl}
              alt={image.altText}
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ${
                index === activeIndex ? 'opacity-100' : 'opacity-0'
              }`}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          ))}
          {activeImage.linkUrl && (
            <a
              href={activeImage.linkUrl}
              className="absolute bottom-3 right-3 inline-flex h-10 items-center gap-1 rounded-full bg-white/90 px-3 text-sm font-medium text-brand-brown shadow transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 dark:bg-gray-900/90 dark:text-gray-100"
              aria-label={activeImage.title || activeImage.altText}
            >
              <span>Open</span>
              <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
          {isPaused && hasControls && (
            <div className="pointer-events-none absolute right-3 top-3 flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white shadow">
              <Pause className="h-3.5 w-3.5" />
              Paused
            </div>
          )}
          {(activeImage.title || activeImage.caption) && (
            <div className="absolute inset-x-0 bottom-0 bg-black/45 px-4 py-3 text-left text-white sm:px-6">
              {activeImage.title && <div className="text-base font-semibold sm:text-lg">{activeImage.title}</div>}
              {activeImage.caption && <div className="mt-1 text-sm text-white/85">{activeImage.caption}</div>}
            </div>
          )}
          {hasControls && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute left-3 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full bg-white/85 text-brand-brown shadow hover:bg-white"
                onClick={goToPrevious}
                aria-label="Previous slider image"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute right-3 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full bg-white/85 text-brand-brown shadow hover:bg-white"
                onClick={goToNext}
                aria-label="Next slider image"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}
        </div>
        {hasControls && renderSidePreview(nextImage, nextIndex, 'Next')}
      </div>
      {hasControls && (
        <div className="mt-4 flex justify-center gap-2">
          {images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              className={`h-2.5 w-2.5 rounded-full transition-colors ${
                index === activeIndex ? 'bg-brand-green' : 'bg-brand-green/25 hover:bg-brand-green/50'
              }`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Show ${dotLabels[index]}`}
              aria-current={index === activeIndex}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HomeImageSlider;
